import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProjects } from '../services/projectService';
import AdminConnectorPage from './AdminConnectorPage';
import AccountPurgeReview from '../components/AccountPurgeReview';
import {
  getTenants,
  getTenant,
  createTenant,
  updateTenant,
  getUsers,
  updateUserRole,
  getPendingTenants,
  approveTenant,
  suspendTenant,
  resetUserPassword,
  assignUserToTenant,
  unassignUserFromTenant,
  deleteUser,
  restoreUser,
  checkUserDeletion,
  permanentlyDeleteUser,
} from '../services/tenantService';

// ─── Module config toggle labels ──────────────────────────────────────────────
const MODULE_LABELS = {
  consultation:  'Consultation',
  communityNews: 'Community News',
  coms:          'Coms',
  communityData: 'Community Data',
  harvesting:    'Harvesting',
  lenses:        'Lenses',
  sevenGen:      'Seven Gen',
  landValues:    'Land Values',
  landUnits:     'Land Units',
};

const GLOBAL_ROLES = ['user', 'proponent', 'cip_staff', 'cip_admin'];
const GLOBAL_ROLE_LABELS = { user: 'User', proponent: 'Proponent', cip_staff: 'CIP Staff', cip_admin: 'CIP Admin' };
const COMMUNITY_ROLES = ['member', 'community_staff', 'community_admin'];
const TENANT_TYPES    = ['organization', 'band', 'first_nation', 'government', 'municipality', 'public'];

// ─── Simple Modal wrapper ─────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-header">
        <h3>{title}</h3>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);

// ─── ResetPasswordModal ───────────────────────────────────────────────────────
const ResetPasswordModal = ({ user, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm]         = useState('');
  const [msg, setMsg]                 = useState('');
  const [success, setSuccess]         = useState(false);
  const [saving, setSaving]           = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (newPassword.length < 8) { setMsg('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirm)  { setMsg('Passwords do not match.');              return; }
    setSaving(true);
    try {
      await resetUserPassword(user._id, newPassword);
      setSuccess(true);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Reset Password — ${user.email}`} onClose={onClose}>
      {success ? (
        <>
          <p className="success-msg">Password reset successfully.</p>
          <button onClick={onClose}>Close</button>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <label>New password
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required autoComplete="new-password" />
          </label>
          <label>Confirm password
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" />
          </label>
          {msg && <p className="error-msg">{msg}</p>}
          <div className="form-actions">
            <button type="submit" disabled={saving}>{saving ? 'Resetting…' : 'Reset Password'}</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
};

// ─── AssignTenantModal ────────────────────────────────────────────────────────
const AssignTenantModal = ({ user, tenants, onClose, onAssigned }) => {
  const [tenantId, setTenantId]           = useState('');
  const [communityRole, setCommunityRole] = useState('member');
  const [msg, setMsg]                     = useState('');
  const [success, setSuccess]             = useState(false);
  const [saving, setSaving]               = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!tenantId) { setMsg('Please select a tenant.'); return; }
    setSaving(true);
    try {
      await assignUserToTenant(user._id, { tenantId, communityRole });
      setSuccess(true);
      onAssigned && onAssigned();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Assignment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Assign to Tenant — ${user.email}`} onClose={onClose}>
      {success ? (
        <>
          <p className="success-msg">User assigned successfully.</p>
          <button onClick={onClose}>Close</button>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <label>Tenant
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} required>
              <option value="">-- Select a tenant --</option>
              {tenants.map((t) => (
                <option key={t._id} value={t._id}>{t.branding?.displayName || t.name} ({t.slug})</option>
              ))}
            </select>
          </label>
          <label>Community Role
            <select value={communityRole} onChange={(e) => setCommunityRole(e.target.value)}>
              {COMMUNITY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          {msg && <p className="error-msg">{msg}</p>}
          <div className="form-actions">
            <button type="submit" disabled={saving}>{saving ? 'Assigning…' : 'Assign'}</button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  );
};
const TenantEditor = ({ initial, onSave, onCancel }) => {
  const isNew = !initial?._id;

  const [form, setForm] = useState({
    name:        initial?.name        || '',
    slug:        initial?.slug        || '',
    description: initial?.description || '',
    type:        initial?.type        || 'organization',
    isActive:    initial?.isActive    !== false,
    settings: {
      allowPublicMode:             initial?.settings?.allowPublicMode             ?? true,
      allowCommunityMode:          initial?.settings?.allowCommunityMode          ?? false,
      allowHybridMode:             initial?.settings?.allowHybridMode             ?? false,
      requireMembershipValidation: initial?.settings?.requireMembershipValidation ?? false,
    },
    moduleConfig: {
      consultation:  { enabled: initial?.moduleConfig?.consultation?.enabled  ?? true  },
      communityNews: { enabled: initial?.moduleConfig?.communityNews?.enabled  ?? true  },
      coms:          { enabled: initial?.moduleConfig?.coms?.enabled           ?? true  },
      communityData: { enabled: initial?.moduleConfig?.communityData?.enabled  ?? false },
      harvesting:    { enabled: initial?.moduleConfig?.harvesting?.enabled     ?? false },
      lenses:        { enabled: initial?.moduleConfig?.lenses?.enabled         ?? false },
      sevenGen:      { enabled: initial?.moduleConfig?.sevenGen?.enabled       ?? false },
      landValues:    { enabled: initial?.moduleConfig?.landValues?.enabled     ?? false },
      landUnits:     { enabled: initial?.moduleConfig?.landUnits?.enabled      ?? false },
    },
    branding: {
      displayName:  initial?.branding?.displayName  || '',
      logoUrl:      initial?.branding?.logoUrl       || '',
      primaryColor: initial?.branding?.primaryColor  || '',
      bannerText:   initial?.branding?.bannerText    || '',
    },
  });

  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setSaving(true);
    try {
      const res = isNew
        ? await createTenant(form)
        : await updateTenant(initial._id, form);
      onSave(res.data);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="tenant-editor" onSubmit={handleSubmit}>
      <div className="tenant-editor__header">
        <div>
          <h3>{isNew ? 'Create Tenant' : initial.name}</h3>
          <p className="tenant-editor__subtitle">
            {isNew ? 'Set up a new community on the platform' : 'Edit community configuration and branding'}
          </p>
        </div>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="Close editor">×</button>
      </div>

      <fieldset>
        <legend>Identity</legend>
        <label>Name
          <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </label>
        <label>Slug (URL key)
          <input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} required disabled={!isNew} />
        </label>
        <label>Type
          <select value={form.type} onChange={(e) => set('type', e.target.value)}>
            {TENANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Description
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} />
        </label>
        <label className="checkbox-label">
          <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          Active
        </label>
      </fieldset>

      <fieldset>
        <legend>Membership Settings</legend>
        {[
          ['allowPublicMode',             'Allow public (anonymous) mode'],
          ['allowCommunityMode',          'Allow community-gated mode'],
          ['allowHybridMode',             'Allow hybrid mode'],
          ['requireMembershipValidation', 'Require membership validation'],
        ].map(([key, label]) => (
          <label key={key} className="checkbox-label">
            <input
              type="checkbox"
              checked={form.settings[key]}
              onChange={(e) => set(`settings.${key}`, e.target.checked)}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Module Config</legend>
        <p className="fieldset-hint">Modules enabled here appear in this community's navigation and tools.</p>
        {Object.entries(MODULE_LABELS).map(([key, label]) => (
          <label key={key} className="checkbox-label">
            <input
              type="checkbox"
              checked={form.moduleConfig[key].enabled}
              onChange={(e) => set(`moduleConfig.${key}.enabled`, e.target.checked)}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Branding</legend>
        <label>Display name (override)
          <input value={form.branding.displayName} onChange={(e) => set('branding.displayName', e.target.value)} placeholder={form.name} />
        </label>
        <label>Logo URL
          <input value={form.branding.logoUrl} onChange={(e) => set('branding.logoUrl', e.target.value)} />
        </label>
        {form.branding.logoUrl && (
          <div className="tenant-editor__logo-preview">
            <img src={form.branding.logoUrl} alt="Logo preview" onError={(e) => { e.target.style.display = 'none'; }} />
            <span>Logo preview</span>
          </div>
        )}
        <label>Primary color
          <div className="tenant-editor__color-row">
            <input
              type="color"
              className="tenant-editor__color-swatch"
              value={/^#[0-9a-fA-F]{6}$/.test(form.branding.primaryColor) ? form.branding.primaryColor : '#2563eb'}
              onChange={(e) => set('branding.primaryColor', e.target.value)}
            />
            <input value={form.branding.primaryColor} onChange={(e) => set('branding.primaryColor', e.target.value)} placeholder="#2B6CB0" />
          </div>
        </label>
        <label>Banner text
          <input value={form.branding.bannerText} onChange={(e) => set('branding.bannerText', e.target.value)} />
        </label>
      </fieldset>

      {msg && <p className="error-msg">{msg}</p>}
      <div className="form-actions">
        <button type="submit" disabled={saving}>{saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

// ─── TenantsTab ───────────────────────────────────────────────────────────────
// Exported for reuse on the standalone Tenants page.
export const TenantsTab = () => {
  const [tenants, setTenants]        = useState([]);
  const [pendingTenants, setPending] = useState([]);
  const [loading, setLoading]        = useState(true);
  const [editing, setEditing]        = useState(null);
  const [selected, setSelected]      = useState(null);
  const [actionMsg, setActionMsg]    = useState('');

  const load = useCallback(() => {
    setLoading(true);
    // allSettled: a 403 on pending (non-admin session) must not blank the tenant list
    Promise.allSettled([getTenants(), getPendingTenants()])
      .then(([approved, pending]) => {
        if (approved.status === 'fulfilled') setTenants(approved.value.data);
        else console.error(approved.reason);
        if (pending.status === 'fulfilled') setPending(pending.value.data);
        else setPending([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (tenant) => {
    try {
      const res = await getTenant(tenant._id);
      setSelected(res.data);
      setEditing('edit');
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaved = (savedTenant) => {
    setTenants((prev) => {
      const idx = prev.findIndex((t) => t._id === savedTenant._id);
      if (idx === -1) return [savedTenant, ...prev];
      const next = [...prev];
      next[idx] = savedTenant;
      return next;
    });
    setEditing(null);
    setSelected(null);
  };

  const handleApprove = async (tenant) => {
    setActionMsg('');
    try {
      await approveTenant(tenant._id);
      setPending((prev) => prev.filter((t) => t._id !== tenant._id));
      const res = await getTenant(tenant._id);
      setTenants((prev) => [res.data, ...prev]);
      setActionMsg(`✔ ${tenant.name} approved`);
    } catch (err) {
      setActionMsg(err.response?.data?.message || 'Approval failed');
    }
  };

  const handleSuspend = async (tenant) => {
    setActionMsg('');
    try {
      await suspendTenant(tenant._id);
      setTenants((prev) => prev.filter((t) => t._id !== tenant._id));
      setPending((prev) => prev.filter((t) => t._id !== tenant._id));
      setActionMsg(`— ${tenant.name} suspended`);
    } catch (err) {
      setActionMsg(err.response?.data?.message || 'Suspend failed');
    }
  };

  if (editing) {
    return (
      <TenantEditor
        initial={editing === 'new' ? null : selected}
        onSave={handleSaved}
        onCancel={() => { setEditing(null); setSelected(null); }}
      />
    );
  }

  if (loading) return <p className="page-loading">Loading tenants…</p>;

  return (
    <section>
      {actionMsg && <p style={{ marginBottom: 8 }}>{actionMsg}</p>}

      {pendingTenants.length > 0 && (
        <div className="pending-section">
          <h3>⏳ Pending Approval ({pendingTenants.length})</h3>
          <table className="admin-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Contact</th><th>Requested</th><th></th></tr>
            </thead>
            <tbody>
              {pendingTenants.map((t) => (
                <tr key={t._id}>
                  <td><strong>{t.name}</strong> <code>{t.slug}</code></td>
                  <td>{t.type || '—'}</td>
                  <td>{t.primaryContact?.email || '—'}</td>
                  <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-sm" onClick={() => handleApprove(t)}>Approve</button>
                    {' '}
                    <button className="btn-secondary btn-sm" onClick={() => handleSuspend(t)}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="section-header">
        <h3>Active Tenants ({tenants.length})</h3>
        <button onClick={() => setEditing('new')}>+ New Tenant</button>
      </div>
      {tenants.length === 0 ? (
        <p>No tenants yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Slug</th>
              <th>Tenant URL</th>
              <th>Modules on</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const onModules = t.moduleConfig
                ? Object.entries(t.moduleConfig).filter(([, v]) => v?.enabled).map(([k]) => MODULE_LABELS[k] || k)
                : [];
              const tenantUrl = `${window.location.origin}/t/${t.slug}/login`;
              return (
                <tr key={t._id}>
                  <td>{t.branding?.displayName || t.name}</td>
                  <td>{t.type || '—'}</td>
                  <td><code>{t.slug}</code></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <a href={`/t/${t.slug}/login`} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem' }}>
                      /t/{t.slug}/login ↗
                    </a>
                    {' '}
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => navigator.clipboard?.writeText(tenantUrl)}
                      title={tenantUrl}
                    >
                      Copy
                    </button>
                  </td>
                  <td>{onModules.length ? onModules.join(', ') : <em>none</em>}</td>
                  <td>{t.isActive ? '✓' : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link
                      to={`/t/${t.slug}`}
                      style={{ marginRight: 6, fontSize: '0.82rem', color: '#4285F4' }}
                    >
                      Visit ↗
                    </Link>
                    <button className="btn-secondary btn-sm" onClick={() => openEdit(t)}>Edit</button>
                    {' '}
                    <button className="btn-secondary btn-sm" onClick={() => handleSuspend(t)}>Suspend</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── UsersTab ─────────────────────────────────────────────────────────────────
export const UsersTab = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers]         = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [resetTarget, setReset]   = useState(null);
  const [assignTarget, setAssign] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [checking, setChecking] = useState(null);
  const [deletionReview, setDeletionReview] = useState(null);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);

  useEffect(() => {
    Promise.all([getUsers(), getTenants()])
      .then(([usersRes, tenantsRes]) => {
        setUsers(usersRes.data);
        setTenants(tenantsRes.data);
      })
      .catch((err) => setMsg(err.response?.data?.message || 'Unable to load users. Reload to retry.'))
      .finally(() => setLoading(false));
  }, []);

  const handleRoleChange = async (userId, globalRole) => {
    setMsg('');
    setDeletionReview(null);
    try {
      const res = await updateUserRole(userId, globalRole);
      setUsers((prev) => prev.map((u) => (u._id === userId ? { ...res.data, memberships: u.memberships } : u)));
    } catch (err) {
      setMsg(err.response?.data?.message || 'Role update failed');
    }
  };

  const handleUnassign = async (user, membershipId) => {
    setMsg('');
    setDeletionReview(null);
    try {
      await unassignUserFromTenant(user._id, membershipId);
      setUsers((prev) =>
        prev.map((u) =>
          u._id === user._id
            ? { ...u, memberships: u.memberships.filter((m) => m.membershipId !== membershipId) }
            : u
        )
      );
    } catch (err) {
      setMsg(err.response?.data?.message || 'Unassign failed');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Remove ${user.email}?\n\nIf any linked data, memberships or history exist, this account will be archived and sign-in disabled. All records will be preserved.\n\nOnly an account with no linked records will be permanently deleted. Permanent deletion cannot be undone.`)) return;
    setMsg('');
    setDeletionReview(null);
    setRemoving(user._id);
    try {
      const res = await deleteUser(user._id);
      if (res.data.outcome === 'archived') {
        setUsers((prev) => prev.map((u) => u._id === user._id ? { ...u, ...res.data.user, memberships: u.memberships } : u));
      } else if (res.data.outcome === 'deleted') {
        setUsers((prev) => prev.filter((u) => u._id !== user._id));
      } else {
        throw new Error('Unexpected account removal response. Reload the user list to check its status.');
      }
      setMsg(res.data.message);
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Account removal failed');
    } finally {
      setRemoving(null);
    }
  };

  const handleRestore = async (user) => {
    if (!window.confirm(`Restore ${user.email}?\n\nThis enables sign-in and restores the account's retained roles and community access, including any staff or administrator permissions. Suspended or pending community memberships remain unchanged.`)) return;
    setMsg('');
    setDeletionReview(null);
    setRestoring(user._id);
    try {
      const res = await restoreUser(user._id);
      setUsers((prev) => prev.map((u) => u._id === user._id
        ? { ...u, ...res.data.user, archivedAt: res.data.user.archivedAt,
          archivedBy: res.data.user.archivedBy, memberships: u.memberships } : u));
      setMsg(res.data.message);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Account restoration failed. No access was restored.');
    } finally {
      setRestoring(null);
    }
  };
  const handleDeletionCheck = async (user) => {
    setMsg('');
    setDeletionReview(null);
    setChecking(user._id);
    try {
      const { data } = await checkUserDeletion(user._id);
      if (data.userId !== user._id || typeof data.canPermanentlyDelete !== 'boolean'
        || !Array.isArray(data.references) || !Array.isArray(data.blockers)) {
        throw new Error('Invalid account check response. Please retry.');
      }
      setDeletionReview({ ...data, user });
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Unable to check linked records. Please retry.');
    } finally {
      setChecking(null);
    }
  };

  const handlePermanentDelete = async () => {
    const { user } = deletionReview;
    if (!window.confirm(`Permanently delete ${user.email} (account ${user._id})?\n\nThis cannot be undone. The server will recheck all linked records and refuse deletion if anything is attached. No memberships or history will be removed.`)) return;
    setMsg('');
    setDeletionReview(null);
    setRemoving(user._id);
    try {
      const { data } = await permanentlyDeleteUser(user._id);
      if (data.outcome !== 'deleted') throw new Error('Unexpected deletion response. Reload the list to check the account.');
      setUsers((prev) => prev.filter((u) => u._id !== user._id));
      setMsg(data.message);
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Permanent deletion failed. Check linked records again before retrying.');
    } finally {
      setRemoving(null);
    }
  };
  const accountBusy = removing !== null || restoring !== null || checking !== null || purgeTarget !== null;
  const identityGroups = new Map();
  users.forEach((user) => {
    if (user.identityUnavailable) return;
    const email = user.email?.trim().toLowerCase();
    const rsminId = user.externalIdentifiers?.rsmin?.userId;
    const keys = [email && `email:${email}`, rsminId && `rsmin:${rsminId}`].filter(Boolean);
    keys.forEach((key) => {
      if (!identityGroups.has(key)) identityGroups.set(key, []);
      identityGroups.get(key).push(user._id);
    });
  });
  const duplicateIds = new Set([...identityGroups.values()].filter((ids) => ids.length > 1).flat());
  const visibleUsers = duplicatesOnly ? users.filter((user) => duplicateIds.has(user._id)) : users;

  if (loading) return <p className="page-loading">Loading users…</p>;

  return (
    <section>
      <h3>Platform Users ({users.length})</h3>
      {users.some((user) => user.identityUnavailable) && (
        <p role="alert">Some identities could not be decrypted. Repair the server PII key configuration before changing these accounts.</p>
      )}
      <p>Removing an account archives it when linked records exist. Archived accounts cannot sign in; their data and history remain intact. A platform administrator can restore an archived account with its retained access.</p>
      <label>
        <input type="checkbox" checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} />
        {' '}Show possible duplicates only ({duplicateIds.size} accounts)
      </label>
      <p>Possible duplicates share an email address or RSMIN sign-in identity, not just a name. Check each account before deciding what to keep.</p>
      {msg && <p role="status">{msg}</p>}
      {purgeTarget && (
        <AccountPurgeReview user={purgeTarget} onClose={() => setPurgeTarget(null)}
          onPurged={(data) => {
            setUsers((prev) => prev.filter((u) => u._id !== purgeTarget._id));
            setDeletionReview(null);
            setPurgeTarget(null);
            setMsg(data.message);
          }} />
      )}
      {deletionReview && (
        <section aria-label="Account deletion review">
          <h4>Linked records for {deletionReview.user.email}</h4>
          <p>Account ID: {deletionReview.user._id}</p>
          {deletionReview.references.length ? (
            <ul>{deletionReview.references.map((reference) => (
              <li key={reference.model}>{reference.model}: {reference.count} linked records</li>
            ))}</ul>
          ) : <p>No linked records found by the account dependency check.</p>}
          {deletionReview.blockers.map((blocker) => <p key={blocker}>{blocker}</p>)}
          <p>Counts include records created or reviewed by this account, not only records assigned to it. No data is removed by this check.</p>
          <button className="btn-danger btn-sm"
            disabled={accountBusy || !deletionReview.canPermanentlyDelete}
            onClick={handlePermanentDelete}>Permanently delete account</button>
          {' '}
          <button className="btn-secondary btn-sm" disabled={accountBusy}
            onClick={() => setDeletionReview(null)}>Close review</button>
          {' '}
          <button className="btn-danger btn-sm"
            disabled={accountBusy || !deletionReview.user.archivedAt || deletionReview.user.identityUnavailable
              || deletionReview.user._id === currentUser?._id}
            onClick={() => { setMsg(''); setPurgeTarget(deletionReview.user); }}>
            Preview purge of account and personal data
          </button>
          <p>A purge removes personal records as well as the account. Archive the account first; shared-data conflicts must be resolved before it can proceed.</p>
        </section>
      )}
      {duplicatesOnly && visibleUsers.length === 0 && <p>No matching duplicate accounts found.</p>}
      <table className="admin-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Global Role</th>
            <th>Status</th>
            <th>Tenants</th>
            <th>Joined</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleUsers.map((u) => (
            <tr key={u._id}>
              <td>{u.email}{duplicateIds.has(u._id) && <small> (Possible duplicate)</small>}</td>
              <td>{u.firstName} {u.lastName}</td>
              <td>
                <select
                  value={u.globalRole || 'user'}
                  disabled={!!u.archivedAt || accountBusy}
                  onChange={(e) => handleRoleChange(u._id, e.target.value)}
                >
                  {GLOBAL_ROLES.map((r) => (
                    <option key={r} value={r}>{GLOBAL_ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </td>
              <td>{u.archivedAt ? 'Archived' : u.isActive === false ? 'Inactive' : 'Active'}</td>
              <td>
                {(u.memberships || []).length === 0
                  ? <em style={{ color: '#94a3b8', fontSize: '0.8rem' }}>none</em>
                  : (u.memberships || []).map((m) => (
                      <span
                        key={m.membershipId}
                        title={`Status: ${m.status}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          margin: '1px 2px',
                          padding: '1px 4px 1px 6px',
                          fontSize: '0.75rem',
                          borderRadius: 10,
                          background: m.status === 'active' ? '#dcfce7' : '#fef9c3',
                          color: m.status === 'active' ? '#166534' : '#854d0e',
                          border: '1px solid',
                          borderColor: m.status === 'active' ? '#86efac' : '#fde68a',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {m.tenantName} ({m.communityRole})
                        <button
                          disabled={!!u.archivedAt || accountBusy}
                          title={`Remove from ${m.tenantName}`}
                          onClick={() => handleUnassign(u, m.membershipId)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '0 2px', fontSize: '0.7rem', lineHeight: 1,
                            color: 'inherit', opacity: 0.65,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))
                }
              </td>
              <td>{new Date(u.createdAt).toLocaleDateString()}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="btn-secondary btn-sm" disabled={accountBusy} onClick={() => handleDeletionCheck(u)}>
                  {checking === u._id ? 'Checking...' : 'Check linked records'}
                </button>
                {' '}
                <button className="btn-secondary btn-sm" disabled={!!u.archivedAt || accountBusy} onClick={() => setReset(u)}>Reset Password</button>
                {' '}
                <button className="btn-secondary btn-sm" disabled={!!u.archivedAt || accountBusy} onClick={() => setAssign(u)}>Assign Tenant</button>
                {' '}
                {u.archivedAt ? <button className="btn-secondary btn-sm"
                  disabled={accountBusy || u.identityUnavailable}
                  onClick={() => handleRestore(u)}>
                  {restoring === u._id ? 'Restoring...' : 'Restore account'}
                </button> : <button className="btn-danger btn-sm"
                  disabled={accountBusy || u._id === currentUser?._id}
                  onClick={() => handleDelete(u)}>
                  {removing === u._id ? 'Removing...' : 'Remove account'}
                </button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {resetTarget  && <ResetPasswordModal user={resetTarget} onClose={() => setReset(null)} />}
      {assignTarget && (
        <AssignTenantModal
          user={assignTarget}
          tenants={tenants}
          onClose={() => setAssign(null)}
          onAssigned={() => { setMsg(`✔ ${assignTarget.email} assigned to tenant`); setAssign(null); }}
        />
      )}
    </section>
  );
};

// ─── ProjectsTab ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  draft:    { bg: '#1e3a5f', color: '#93c5fd' },
  active:   { bg: '#14532d', color: '#86efac' },
  closed:   { bg: '#3f3f46', color: '#a1a1aa' },
  archived: { bg: '#292524', color: '#a8a29e' },
};

const ProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [tenants,  setTenants2] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTenant, setFilterTenant] = useState('');

  useEffect(() => {
    Promise.all([getProjects(), getTenants()])
      .then(([pRes, tRes]) => {
        setProjects(pRes.data);
        setTenants2(tRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const visible = projects.filter(p => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterTenant && p.tenantId?._id !== filterTenant) return false;
    return true;
  });

  if (loading) return <p className="page-loading">Loading projects…</p>;

  const selStyle = {
    background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
    color: '#e2e8f0', padding: '5px 9px', fontSize: 12,
  };

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>All Projects ({visible.length})</h3>
        <select style={selStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['draft','active','closed','archived'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select style={selStyle} value={filterTenant} onChange={e => setFilterTenant(e.target.value)}>
          <option value="">All Communities</option>
          {tenants.map(t => (
            <option key={t._id} value={t._id}>{t.branding?.displayName || t.name}</option>
          ))}
        </select>
      </div>

      {visible.length === 0 ? (
        <p style={{ color: '#64748b' }}>No projects match the current filters.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Community</th>
              <th>Proponent Org</th>
              <th>Type</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(p => {
              const sc = STATUS_COLORS[p.status] || STATUS_COLORS.draft;
              return (
                <tr key={p._id}>
                  <td>
                    <Link to={`/projects/${p._id}`} style={{ color: '#7dd3fc', textDecoration: 'none' }}>
                      {p.title}
                    </Link>
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 7px', borderRadius: 10, fontSize: '0.75rem',
                      fontWeight: 600, background: sc.bg, color: sc.color,
                    }}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {p.tenantId?.branding?.displayName || p.tenantId?.name || '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>{p.proponentOrg || '—'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{p.type || '—'}</td>
                  <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── AdminPage ────────────────────────────────────────────────────────────────
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="page admin-page">
      <h2>Platform Admin</h2>

      <div className="tabs">
        <button className={activeTab === 'users'    ? 'active' : ''} onClick={() => setActiveTab('users')}>
          Users
        </button>
        <button className={activeTab === 'projects' ? 'active' : ''} onClick={() => setActiveTab('projects')}>
          Projects
        </button>
        <button className={activeTab === 'connector' ? 'active' : ''} onClick={() => setActiveTab('connector')}>
          ⚙️ Connector Config
        </button>
      </div>

      {activeTab === 'users'     && <UsersTab />}
      {activeTab === 'projects'  && <ProjectsTab />}
      {activeTab === 'connector' && <AdminConnectorPage />}
    </div>
  );
};

export default AdminPage;
